import { useState, useMemo } from 'react';
import SEOHead from '../../components/feature/SEOHead';
import { blogArticles } from '../../mocks/blog';
import BlogHero from './components/BlogHero';
import CategoryFilter from './components/CategoryFilter';
import FeaturedArticle from './components/FeaturedArticle';
import ArticleCard from './components/ArticleCard';
import BlogNewsletter from './components/BlogNewsletter';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const blogSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Blog | Carta do Eu Futuro',
    url: `${SITE_URL}/blog`,
    inLanguage: 'pt-BR',
    description: 'Artigos sobre desenvolvimento pessoal, PNL, gratidão, meditação e técnicas de transformação.',
    publisher: {
      '@type': 'Organization',
      name: 'Carta do Eu Futuro',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/logo.png`,
      },
    },
    author: {
      '@type': 'Person',
      name: 'Bia Holmes',
      url: `${SITE_URL}/sobre`,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Blog | Carta do Eu Futuro',
    url: `${SITE_URL}/blog`,
    inLanguage: 'pt-BR',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
      ],
    },
  },
];

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('todos');
  const [searchQuery, setSearchQuery] = useState('');

  // Get featured article
  const featuredArticle = useMemo(() => {
    return blogArticles.find((article) => article.featured);
  }, []);

  // Filter articles
  const filteredArticles = useMemo(() => {
    let filtered = blogArticles.filter((article) => !article.featured);

    // Filter by category
    if (activeCategory !== 'todos') {
      filtered = filtered.filter(
        (article) => article.category.toLowerCase().replace(/\s+/g, '-') === activeCategory
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          article.excerpt.toLowerCase().includes(query) ||
          article.category.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [activeCategory, searchQuery]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setActiveCategory('todos');
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <SEOHead
        title="Blog | Desenvolvimento Pessoal, PNL e Gratidão — Carta do Eu Futuro"
        description="Artigos sobre desenvolvimento pessoal, PNL, gratidão, meditação guiada e técnicas de transformação. Aprenda com Bia Holmes e transforme sua vida."
        keywords="blog desenvolvimento pessoal, PNL artigos, gratidão, meditação, transformação pessoal"
        canonical="/blog"
        ogImage="https://public.readdy.ai/ai/img_res/e53f9e5b92fb0a2a3f16b90d65c274c0.jpg"
        ogType="website"
        schema={blogSchema}
      />
      <Navbar />

      {/* Hero Section */}
      <BlogHero onSearch={handleSearch} />

      {/* Category Filter */}
      <CategoryFilter activeCategory={activeCategory} onCategoryChange={setActiveCategory} />

      {/* Main Content */}
      <main className="py-16">
        <div className="container mx-auto px-6">
          {/* Featured Article */}
          {featuredArticle && !searchQuery && activeCategory === 'todos' && (
            <div className="mb-16">
              <FeaturedArticle article={featuredArticle} />
            </div>
          )}

          {/* Search Results Info */}
          {searchQuery && (
            <div className="mb-8">
              <p className="text-charcoal-700">
                Encontrados <span className="font-bold text-gold-600">{filteredArticles.length}</span> artigos para "{searchQuery}"
              </p>
            </div>
          )}

          {/* Articles Grid */}
          {filteredArticles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-cream-100 rounded-full mb-6">
                <i className="ri-search-line text-4xl text-gold-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-charcoal-900 mb-3">Nenhum artigo encontrado</h3>
              <p className="text-charcoal-500 mb-6">
                Tente buscar por outros termos ou explore diferentes categorias.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('todos');
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gold-500 to-gold-600 text-white font-semibold rounded-xl hover:from-gold-600 hover:to-gold-700 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                Ver todos os artigos
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Newsletter Section */}
      <BlogNewsletter />

      <Footer />
    </div>
  );
}
